import { CheckCircle, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import styled, { css } from 'styled-components';
import api from '../../services/api';
import useToast from '../../hooks/useToast';
import { Question } from '../../models/Quiz';
import { PageViewSkeleton } from '../../components/Skeleton';

interface ResultProps {
  questions: Question[];
  studentAnswers: { [questionId: string]: string };
  score: number;
}

export const QuizResultComponent = ({
  questions,
  studentAnswers,
  score,
}: ResultProps) => {
  const answersObject = JSON.parse(String(studentAnswers));
  const [isLoading, setIsLoading] = useState(true);
  const { addToast } = useToast();
  const { push } = useHistory();
  const [isAnswerCorrect, setIsAnswerCorrect] = useState<{
    [questionId: string]: boolean;
  }>({});

  const checkAnswers = useCallback(async () => {
    try {
      const { data } = await api.post(
        'question/check-answers',
        {
          answers: answersObject,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      setIsAnswerCorrect(data);
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Erro ao verificar respostas',
        description:
          'Não foi possível verificar as respostas, tente novamente.',
      });
    }
  }, [addToast, answersObject]);

  useEffect(() => {
    checkAnswers();
    setIsLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getEncouragement = (studentScore: number) => {
    if (studentScore >= 80) return 'Excelente trabalho! ';
    if (studentScore >= 60) return 'Bom trabalho! ';
    if (studentScore >= 55) return 'Você está indo bem! ';
    if (studentScore >= 30) return 'Você pode melhorar!';
    return 'Continue tentando! ';
  };

  if (isLoading) {
    return <PageViewSkeleton isLoading={isLoading} />;
  }

  const total = questions.length;

  return (
    <PageWrapper>
      <Container>
        <ResultsCard>
          <Title>Resultados</Title>
          <TotalCorrectAnswers>
            Você acertou{' '}
            {
              Object.values(isAnswerCorrect).filter(value => value === true)
                .length
            }{' '}
            de {total} questões
          </TotalCorrectAnswers>

          <Encouragement>
            <EncouragementTitle>{getEncouragement(score)}</EncouragementTitle>
            <Score>Você tirou {score / 10} de 10!</Score>
            <ProgressBarWrapper>
              <ProgressBar fill={score} />
            </ProgressBarWrapper>
          </Encouragement>

          {questions.map((question, index) => {
            const studentAnswerOption = answersObject[
              question.id
            ] as keyof Question;

            const studentAnswer = question[studentAnswerOption];
            const isCorrect = isAnswerCorrect[question.id] || false;

            return (
              <ResultItem key={question.id}>
                <QuestionText>
                  {index + 1}. {question.description}
                </QuestionText>
                <AnswerRow>
                  <AnswerText>
                    Sua resposta: <strong>{studentAnswer}</strong>
                  </AnswerText>
                  {isCorrect ? (
                    <CheckCircle width={24} height={24} color="green" />
                  ) : (
                    <XCircle width={24} height={24} color="red" />
                  )}
                </AnswerRow>
              </ResultItem>
            );
          })}

          <NavButtons>
            <BackLink onClick={() => push('/quiz')}>Voltar</BackLink>
          </NavButtons>
        </ResultsCard>
      </Container>
    </PageWrapper>
  );
};

const Title = styled.h1`
  font-size: 2.25rem;
  font-weight: bold;
  text-align: center;
  color: #7e22ce;
  margin-bottom: 1.5rem;
`;
const TotalCorrectAnswers = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0.25rem 0;
`;

const Container = styled.div`
  max-width: 768px;
  margin: 0 auto;
  background-color: ${({ theme }) => theme.colors.background};
  padding: 0 1rem;
`;

const CardBase = css`
  background-color: #ffffff;
  border-radius: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
`;

const QuizCard = styled.div`
  ${CardBase}
`;

const ResultsCard = styled(QuizCard)`
  padding: 2rem;
`;
const NavButtons = styled.div`
  display: flex;
  justify-content: space-between;
`;

const NavButton = styled.button<{
  variant: 'back' | 'next';
  disabled: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 1.2rem;
  font-size: 1.125rem;
  font-weight: bold;
  border: none;
  cursor: pointer;

  ${props =>
    props.disabled &&
    css`
      background-color: #e5e7eb;
      color: #9ca3af;
      cursor: not-allowed;
    `}

  ${props =>
    !props.disabled &&
    props.variant === 'back' &&
    css`
      background-color: #3b82f6;
      color: #ffffff;
      &:hover {
        background-color: #2563eb;
      }
    `}
  
    ${props =>
    !props.disabled &&
    props.variant === 'next' &&
    css`
      background-color: #10b981;
      color: #ffffff;
      &:hover {
        background-color: #059669;
      }
    `}
`;

const Encouragement = styled.div`
  background-color: #fef3c7;
  border-radius: 1rem;
  padding: 1.5rem;
  text-align: center;
  margin-bottom: 2rem;
`;

const EncouragementTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  color: #b45309;
  margin-bottom: 0.5rem;
`;

const Score = styled.p`
  font-size: 2rem;
  font-weight: bold;
  color: #7e22ce;
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  background-color: #e5e7eb;
  border-radius: 9999px;
  height: 1.5rem;
  margin-top: 1rem;
`;

const ProgressBar = styled.div<{ fill: number }>`
  height: 100%;
  border-radius: 9999px;
  background-color: #10b981;
  width: ${props => props.fill}%;
`;

const ResultItem = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const QuestionText = styled.p`
  font-weight: bold;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
`;

const AnswerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AnswerText = styled.p`
  font-weight: 500;
`;

const CorrectAnswer = styled.p`
  color: #059669;
  font-weight: 500;
  margin-top: 0.5rem;
`;

const BackLink = styled.button`
  background-color: #7e22ce;
  color: #ffffff;
  padding: 1rem 2rem;
  border-radius: 9999px;
  font-size: 1.25rem;
  font-weight: bold;
  border: none;
  cursor: pointer;
  &:hover {
    background-color: #6b21a8;
  }
`;
